import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PlugZap, RefreshCw, Save, Server, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, Card, Field } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { ConfirmButton } from "@/components/ranger/ConfirmButton";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime, rconChanged } from "@/lib/api";

const inputCls = "border-[#1E2D4A] bg-[#050811] font-mono";
const btn = "flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors disabled:opacity-40";
export const BTN_PRIMARY = `${btn} bg-[#E5202E] text-white hover:bg-[#FF3B47]`;
export const BTN_SECONDARY = `${btn} border border-[#1E2D4A] bg-[#111A2E] text-slate-200 hover:border-[#E5202E]/40`;
export const BTN_DANGER = `${btn} border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20`;

const TestResult = ({ result }) => (
  <div data-testid="rcon-test-result" className={`mt-4 rounded-sm border p-4 font-mono text-xs ${result.ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-red-500/30 bg-red-500/5 text-red-300"}`}>
    <div className="font-semibold uppercase tracking-widest">{result.ok ? `Connected · ${result.connect_ms}ms` : "Connection failed"}</div>
    {result.error && <div className="mt-2 break-words">{result.error}</div>}
    {result.warning && <div className="mt-2 text-amber-300">{result.warning}</div>}
    {result.response && <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-slate-300">{result.response}</pre>}
  </div>
);

export default function Servers() {
  const { active, can } = useAuth();
  const [c, setC] = useState(null);
  const [form, setForm] = useState({ host: "", port: "", password: "" });
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const admin = can("admin");

  const load = useCallback(() => api.get(`/communities/${active.id}`).then((r) => {
    setC(r.data);
    return r.data;
  }), [active.id]);

  useEffect(() => {
    setResult(null);
    load().then((d) => setForm({ host: d.rcon.host || "", port: d.rcon.port || "", password: "" }));
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(ok); return r; } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); load(); rconChanged(); }
  };

  const save = (e) => {
    e.preventDefault();
    run("save", () => api.put(`/communities/${active.id}/rcon/config`, { host: form.host, port: Number(form.port), password: form.password || null }), "RCON settings saved. Connecting…")
      .then(() => setForm((f) => ({ ...f, password: "" })));
  };
  const test = () => run("test", () => api.post(`/communities/${active.id}/rcon/test`)).then((r) => r && setResult(r.data));
  const reconnect = () => run("reconnect", () => api.post(`/communities/${active.id}/rcon/reconnect`), "Reconnect requested");
  const clear = () => run("clear", () => api.delete(`/communities/${active.id}/rcon/config`), "RCON settings cleared")
    .then(() => setForm({ host: "", port: "", password: "" }));

  if (!c) return null;
  const rs = c.rcon_status;
  return (
    <div>
      <PageHeader eyebrow="G-Portal WebRCON" title="Servers" description="Connection settings are stored encrypted on the server. The RCON password is never sent back to the browser." />
      <div className="grid gap-5 lg:grid-cols-[3fr_2fr]">
        <Card title="Connection settings" icon={Server}>
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
              <Field label="Host / IP"><Input data-testid="rcon-host-input" required disabled={!admin} value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="123.45.67.89" className={inputCls} /></Field>
              <Field label="RCON port"><Input data-testid="rcon-port-input" required type="number" min={1} max={65535} disabled={!admin} value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} placeholder="28016" className={inputCls} /></Field>
            </div>
            <Field label="RCON password" hint={c.rcon.password_set ? "A password is stored. Leave blank to keep it." : "Enable RCON-Web in G-Portal and set a password first."}>
              <Input data-testid="rcon-password-input" type="password" autoComplete="new-password" disabled={!admin} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={c.rcon.password_set ? "•••••••• stored" : "Required"} className={inputCls} />
            </Field>
            {admin ? (
              <div className="flex flex-wrap gap-2 pt-2">
                <button data-testid="rcon-save-button" disabled={!!busy} className={BTN_PRIMARY}><Save className="h-4 w-4" />{busy === "save" ? "Saving…" : "Save & connect"}</button>
                <button type="button" data-testid="test-connection-button" disabled={!!busy || !c.rcon.configured} onClick={test} className={BTN_SECONDARY}><PlugZap className="h-4 w-4" />{busy === "test" ? "Testing…" : "Test connection"}</button>
                {c.rcon.configured && <ConfirmButton testId="rcon-clear-button" className={BTN_DANGER} title="Clear RCON settings?" description="RANGER will disconnect from this server and delete the stored password." onConfirm={clear}><Trash2 className="h-4 w-4" />Clear</ConfirmButton>}
              </div>
            ) : <p className="text-xs text-slate-500">Admin role required to change connection settings.</p>}
          </form>
          {result && <TestResult result={result} />}
        </Card>
        <Card title="Live link" right={<StatusBadge state={rs.state} testId="rcon-status-badge" />}>
          <dl className="space-y-3 text-sm">
            <div><dt className="rg-label">Endpoint</dt><dd className="font-mono text-slate-300">{c.rcon.configured ? `ws://${c.rcon.host}:${c.rcon.port}` : "—"}</dd></div>
            <div><dt className="rg-label">Connected since</dt><dd className="text-slate-300">{fmtTime(rs.connected_at)}</dd></div>
            <div><dt className="rg-label">Last message</dt><dd className="text-slate-300">{fmtTime(rs.last_message_at)}</dd></div>
            <div><dt className="rg-label">Failed attempts</dt><dd className="font-mono text-slate-300">{rs.reconnect_attempts}</dd></div>
            {rs.error && <div><dt className="rg-label">Last error</dt><dd data-testid="rcon-error" className="break-words font-mono text-xs text-red-400">{rs.error}</dd></div>}
          </dl>
          {admin && c.rcon.configured && <button data-testid="rcon-reconnect-button" onClick={reconnect} disabled={!!busy} className={`${BTN_SECONDARY} mt-5`}><RefreshCw className="h-4 w-4" />Reconnect</button>}
          <p className="mt-5 text-xs text-slate-500">Test on a staging server before pointing RANGER at a live server.</p>
        </Card>
      </div>
    </div>
  );
}
